/**
 * Die Cut Shape Selector
 * ----------------------
 * Custom element that handles:
 * - Collapsible shape picker (uses native <details>, with progressive enhancements
 *   for summary pill updates and auto-collapse on selection)
 * - Single-select shape picking (radio buttons)
 * - Adding the selected shape as a separate cart line item
 * - Clearing selection
 *
 * Hooks into Impact theme's variant:add event on the product form
 * to add the die cut shape after the main product is added to cart.
 */

const AUTO_COLLAPSE_DELAY_MS = 250;

class DieCutSelector extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('.die-cut-selector__details');
    this.summary = this.querySelector('.die-cut-selector__summary');
    this.selectedWrap = this.querySelector('.die-cut-selector__selected');
    this.selectedLabel = this.querySelector('.die-cut-selector__selected-label');
    this.selectedThumb = this.querySelector('.die-cut-selector__selected-thumb');
    this.selectedImg = this.querySelector('.die-cut-selector__selected-img');
    this.hiddenInput = this.querySelector('.die-cut-selector__input');
    this.clearBtn = this.querySelector('.die-cut-selector__clear');
    this.expandBtn = this.querySelector('.die-cut-selector__expand');
    this.radios = this.querySelectorAll('input[name="die-cut-shape"]');
    this.emptyText = this.selectedWrap?.dataset.emptyText || 'No shape selected';

    this.radios.forEach((radio) => {
      radio.addEventListener('change', this.onSelectionChange.bind(this));
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', this.onClear.bind(this));
    }

    if (this.expandBtn) {
      this.expandBtn.addEventListener('click', this.onExpand.bind(this));
    }

    // Reset to the clipped ("row 1 only") view whenever the user manually
    // collapses the picker via the summary, so re-opening always lands on
    // the consistent peek state.
    if (this.details) {
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) {
          this.collapseToRowOne();
        }
      });
    }

    // Find the product form — Impact uses is="product-form" on <form> elements
    this.productForm = document.querySelector(`form[is="product-form"]`)
      || this.closest('form')
      || document.querySelector(`form[id^="product-form-"]`);

    if (this.productForm) {
      // Listen for Impact's variant:add event (fires after successful cart add)
      this.productForm.addEventListener('variant:add', this.onVariantAdded.bind(this));
    }
  }

  get selectedVariantId() {
    const checked = this.querySelector('input[name="die-cut-shape"]:checked');
    return checked ? checked.value : null;
  }

  /**
   * Update the summary pill to reflect the currently-selected shape.
   * If `radio` is null, resets to the empty state.
   */
  updateSummary(radio) {
    if (!this.selectedLabel) return;

    if (!radio) {
      this.selectedLabel.textContent = this.emptyText;
      this.selectedLabel.classList.add('text-subdued');
      if (this.selectedThumb) {
        this.selectedThumb.hidden = true;
        if (this.selectedImg) this.selectedImg.src = '';
      }
      return;
    }

    // Pull the selected shape's title + image from the option label DOM
    const label = radio.closest('label.die-cut-shape-option');
    const titleEl = label?.querySelector('.die-cut-shape-option__label');
    const thumbEl = label?.querySelector('.die-cut-shape-option__image img');
    const title = titleEl ? titleEl.textContent.trim() : (radio.getAttribute('aria-label') || 'Selected');

    this.selectedLabel.textContent = title;
    this.selectedLabel.classList.remove('text-subdued');

    if (this.selectedThumb && this.selectedImg) {
      if (thumbEl && thumbEl.src) {
        this.selectedImg.src = thumbEl.src;
        this.selectedImg.alt = title;
        this.selectedThumb.hidden = false;
      } else {
        this.selectedThumb.hidden = true;
        this.selectedImg.src = '';
      }
    }
  }

  onSelectionChange(event) {
    const radio = event.target;
    this.hiddenInput.value = radio.value;

    if (this.clearBtn) {
      this.clearBtn.classList.remove('hidden');
    }

    this.updateSummary(radio);

    // Auto-collapse the panel after selection so the page footprint shrinks back
    if (this.details && this.details.open) {
      window.setTimeout(() => {
        if (this.details && this.details.open) {
          this.details.open = false;
          // The toggle handler will reset is-expanded for the next open.
        }
      }, AUTO_COLLAPSE_DELAY_MS);
    }
  }

  onClear() {
    this.radios.forEach((radio) => {
      radio.checked = false;
    });
    this.hiddenInput.value = '';

    if (this.clearBtn) {
      this.clearBtn.classList.add('hidden');
    }

    this.updateSummary(null);
  }

  onExpand() {
    this.classList.add('is-expanded');
    if (this.expandBtn) {
      this.expandBtn.setAttribute('aria-expanded', 'true');
    }
  }

  collapseToRowOne() {
    this.classList.remove('is-expanded');
    if (this.expandBtn) {
      this.expandBtn.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * After the main product is added to cart, add the selected die cut shape
   * as a separate line item via the Cart API.
   */
  async onVariantAdded(event) {
    const dieCutVariantId = this.selectedVariantId;
    if (!dieCutVariantId) return;

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(dieCutVariantId, 10),
              quantity: 1,
              properties: {
                '_associated_product': this.getParentProductTitle()
              }
            }
          ]
        })
      });

      if (response.ok) {
        // Refresh cart state so drawer/counter updates with the shape
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        }));
      }
    } catch (error) {
      console.error('[DieCutSelector] Failed to add shape to cart:', error);
    }
  }

  getParentProductTitle() {
    const titleEl = this.closest('.product-info, .product, [class*="product"]')
      ?.querySelector('[class*="title"] a, [class*="title"]');
    return titleEl ? titleEl.textContent.trim() : '';
  }
}

if (!customElements.get('die-cut-selector')) {
  customElements.define('die-cut-selector', DieCutSelector);
}
